import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Loader2, Users, Trophy, Goal, Shield, Waves, Timer, Bike, Dumbbell, Medal,
  CheckCircle2, XCircle, Clock, CreditCard, AlertCircle, LogOut, Wallet,
  ChevronDown, ChevronUp, Copy, Banknote, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Función para formatear RUT chileno
const formatRut = (value) => {
  let rut = value.replace(/[^0-9kK]/g, '').toUpperCase();
  if (rut.length === 0) return '';
  let dv = rut.slice(-1);
  let cuerpo = rut.slice(0, -1);
  let cuerpoFormateado = '';
  let contador = 0;
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    cuerpoFormateado = cuerpo[i] + cuerpoFormateado;
    contador++;
    if (contador === 3 && i !== 0) {
      cuerpoFormateado = '.' + cuerpoFormateado;
      contador = 0;
    }
  }
  if (cuerpo.length === 0) return dv;
  return cuerpoFormateado + '-' + dv;
};

const DISCIPLINA_ICONS = {
  futbol: Goal,
  tenis: Trophy,
  basquetbol: Trophy,
  voleibol: Trophy,
  karate: Shield,
  natacion: Waves,
  atletismo: Timer,
  ciclismo: Bike,
  crossfit: Dumbbell,
  triathlon: Medal,
  otro: Trophy
};

export default function ConsultaPage() {
  const { clubId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [clubInfo, setClubInfo] = useState(null);
  const [error, setError] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [showPagadas, setShowPagadas] = useState(false);
  const [showPagoDialog, setShowPagoDialog] = useState(false);
  const [transaccionPago, setTransaccionPago] = useState(null);
  const [generandoCodigo, setGenerandoCodigo] = useState(false);

  const [formData, setFormData] = useState({
    rut: '',
    email: ''
  });

  const [actualizando, setActualizando] = useState(false);

  useEffect(() => {
    loadClubInfo();
  }, [clubId]);

  const loadClubInfo = async () => {
    try {
      const res = await axios.get(`${API}/consulta/${clubId}/info`);
      setClubInfo(res.data);
    } catch (err) {
      setError('Club no encontrado');
    } finally {
      setLoading(false);
    }
  };

  const handleConsulta = async (e) => {
    if (e) e.preventDefault();
    if (!formData.rut.trim() || !formData.email.trim()) {
      toast.error('Por favor ingresa tu RUT y correo electrónico');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/consulta/${clubId}`, formData);
      setResultado(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se encontraron datos con la información proporcionada');
    } finally {
      setSubmitting(false);
    }
  };

  const handleActualizar = async () => {
    setActualizando(true);
    try {
      const res = await axios.post(`${API}/consulta/${clubId}`, formData);
      setResultado(res.data);
      toast.success('Información actualizada');
    } catch (err) {
      toast.error('Error al actualizar');
    } finally {
      setActualizando(false);
    }
  };

  const handleLogout = () => {
    setResultado(null);
    setFormData({ rut: '', email: '' });
    setTransaccionPago(null);
  };

  const handleGenerarCodigoPago = async () => {
    if (!resultado?.integrante?.id) return;
    
    // Obtener cuotas pendientes
    const cuotasPendientes = resultado.cuotas?.filter(c => c.estado !== 'pagada') || [];
    if (cuotasPendientes.length === 0) {
      toast.info('No tienes cuotas pendientes');
      return;
    }

    setGenerandoCodigo(true);
    try {
      const res = await axios.post(`${API}/public/club/${clubId}/generar-codigo-pago`, {
        integrante_id: resultado.integrante.id,
        cuota_ids: cuotasPendientes.map(c => c.id),
        rut: formData.rut,
        email: formData.email
      });
      setTransaccionPago(res.data);
      setShowPagoDialog(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al generar código de pago');
    } finally {
      setGenerandoCodigo(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copiado al portapapeles');
    } catch (err) {
      toast.error('No se pudo copiar');
    }
  };

  const DisciplinaIcon = clubInfo?.disciplina ? (DISCIPLINA_ICONS[clubInfo.disciplina] || Trophy) : Users;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="p-8 bg-slate-800 border-slate-700 text-center">
          <Loader2 className="w-12 h-12 text-purple-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-300">Cargando...</p>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-red-900/20 to-slate-900 flex items-center justify-center p-4">
        <Card className="p-8 bg-slate-800 border-slate-700 text-center max-w-md">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Error</h2>
          <p className="text-slate-400">{error}</p>
        </Card>
      </div>
    );
  }

  // Vista de resultados
  if (resultado) {
    const { integrante, cuotas, pagos, resumen, mensaje } = resultado;
    
    // Separar cuotas
    const cuotasPendientes = cuotas?.filter(c => c.estado === 'pendiente' || c.estado === 'vencida' || c.estado === 'parcial') || [];
    const cuotasPagadas = cuotas?.filter(c => c.estado === 'pagada') || [];
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-6 px-4">
        <div className="max-w-2xl mx-auto space-y-4">
          
          {/* Header con info del club */}
          <Card className="p-4 bg-slate-800 border-slate-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {clubInfo?.logo ? (
                  <img src={clubInfo.logo} alt="Logo" className="w-10 h-10 rounded-lg object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-purple-600/30 flex items-center justify-center">
                    <DisciplinaIcon className="w-5 h-5 text-purple-400" />
                  </div>
                )}
                <div>
                  <h1 className="text-base font-bold text-white">{clubInfo?.nombre}</h1>
                  <p className="text-xs text-purple-400">Portal de Consulta</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  size="sm" 
                  onClick={handleActualizar} 
                  disabled={actualizando} 
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {actualizando ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                  Actualizar
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleLogout} 
                  variant="outline"
                  className="border-red-500 text-red-400 hover:bg-red-500/20"
                >
                  <LogOut className="w-4 h-4 mr-1" />
                  Salir
                </Button>
              </div>
            </div>
          </Card>

          {/* Info del Integrante */}
          <Card className="p-4 bg-slate-800 border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-purple-600/30 flex items-center justify-center">
                <span className="text-lg font-bold text-purple-400">
                  {integrante.nombre?.charAt(0)}{integrante.apellido?.charAt(0)}
                </span>
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-bold text-white">{integrante.nombre} {integrante.apellido}</h2>
                <p className="text-sm text-slate-400">{integrante.rut}</p>
              </div>
              <Badge className={integrante.estado_inscripcion === 'aprobado' ? 'bg-emerald-600' : 'bg-yellow-600'}>
                {integrante.estado_inscripcion === 'aprobado' ? 'Activo' : integrante.estado_inscripcion}
              </Badge>
            </div>
            
            {integrante.categorias?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-700">
                <div className="flex flex-wrap gap-2">
                  {integrante.categorias.map(cat => (
                    <Badge key={cat.id} variant="outline" className="border-purple-500/50 text-purple-300 text-xs">
                      {cat.nombre}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {mensaje && (
            <div className="p-3 bg-yellow-600/20 border border-yellow-600/30 rounded-lg">
              <p className="text-yellow-300 text-sm">{mensaje}</p>
            </div>
          )}

          {integrante.estado_inscripcion === 'aprobado' && (
            <>
              {/* Resumen de Deuda */}
              {resumen && (
                <Card className="p-4 bg-slate-800 border-slate-700">
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className={`p-3 rounded-lg ${resumen.total_deuda > 0 ? 'bg-red-500/20 border border-red-500/30' : 'bg-emerald-500/20 border border-emerald-500/30'}`}>
                      <p className="text-xs text-slate-400 mb-1">Deuda Total</p>
                      <p className={`text-xl font-bold ${resumen.total_deuda > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                        ${(resumen.total_deuda || 0).toLocaleString('es-CL')}
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-yellow-500/20 border border-yellow-500/30">
                      <p className="text-xs text-slate-400 mb-1">Pendientes</p>
                      <p className="text-xl font-bold text-yellow-400">{resumen.cuotas_pendientes || 0}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-orange-500/20 border border-orange-500/30">
                      <p className="text-xs text-slate-400 mb-1">Vencidas</p>
                      <p className="text-xl font-bold text-orange-400">{resumen.cuotas_vencidas || 0}</p>
                    </div>
                  </div>
                  
                  {/* Botón de Pagar */}
                  {resumen.total_deuda > 0 && (
                    <Button 
                      onClick={handleGenerarCodigoPago}
                      disabled={generandoCodigo}
                      className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white"
                      data-testid="btn-pagar-transferencia"
                    >
                      {generandoCodigo ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Banknote className="w-4 h-4 mr-2" />
                      )}
                      Pagar por Transferencia
                    </Button>
                  )}
                </Card>
              )}

              {/* Cuotas Pendientes */}
              {cuotasPendientes.length > 0 && (
                <Card className="bg-slate-800 border-slate-700 overflow-hidden">
                  <div className="p-3 bg-red-500/10 border-b border-slate-700">
                    <h3 className="font-semibold text-white flex items-center gap-2 text-sm">
                      <AlertCircle className="w-4 h-4 text-red-400" />
                      Cuotas Pendientes ({cuotasPendientes.length})
                    </h3>
                  </div>
                  <div className="divide-y divide-slate-700/50">
                    {cuotasPendientes.map(cuota => {
                      const montoPendiente = (cuota.monto_final || 0) - (cuota.monto_pagado || 0);
                      return (
                        <div key={cuota.id} className="p-3 flex items-center justify-between">
                          <div>
                            <p className="text-white text-sm font-medium">{cuota.descripcion}</p>
                            <p className="text-xs text-slate-400">Vence: {cuota.fecha_vencimiento}</p>
                            {cuota.motivo_descuento && (
                              <p className="text-xs text-green-400">{cuota.motivo_descuento}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-white font-bold">${montoPendiente.toLocaleString('es-CL')}</p>
                            <Badge className={cuota.estado === 'vencida' ? 'bg-red-600 text-xs' : 'bg-yellow-600 text-xs'}>
                              {cuota.estado === 'vencida' ? 'Vencida' : cuota.estado === 'parcial' ? 'Parcial' : 'Pendiente'}
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              )}

              {/* Cuotas Pagadas (colapsable) */}
              {cuotasPagadas.length > 0 && (
                <Card className="bg-slate-800 border-slate-700 overflow-hidden">
                  <button 
                    onClick={() => setShowPagadas(!showPagadas)}
                    className="w-full p-3 bg-emerald-500/10 border-b border-slate-700 flex items-center justify-between hover:bg-emerald-500/20 transition-colors"
                  >
                    <h3 className="font-semibold text-white flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      Cuotas Pagadas ({cuotasPagadas.length})
                    </h3>
                    {showPagadas ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </button>
                  {showPagadas && (
                    <div className="divide-y divide-slate-700/50">
                      {cuotasPagadas.map(cuota => (
                        <div key={cuota.id} className="p-3 flex items-center justify-between">
                          <div>
                            <p className="text-slate-300 text-sm">{cuota.descripcion}</p>
                            <p className="text-xs text-slate-500">{cuota.fecha_vencimiento}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-emerald-400 font-bold">${(cuota.monto_final || 0).toLocaleString('es-CL')}</p>
                            <Badge className="bg-emerald-600 text-xs">Pagada</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              )}

              {/* Historial de Pagos */}
              {pagos && pagos.length > 0 && (
                <Card className="bg-slate-800 border-slate-700 overflow-hidden">
                  <div className="p-3 bg-blue-500/10 border-b border-slate-700">
                    <h3 className="font-semibold text-white flex items-center gap-2 text-sm">
                      <Wallet className="w-4 h-4 text-blue-400" />
                      Historial de Pagos ({pagos.length})
                    </h3>
                  </div>
                  <div className="divide-y divide-slate-700/50">
                    {pagos.map(pago => (
                      <div key={pago.id} className="p-3 flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm">{pago.glosa}</p>
                          <p className="text-xs text-slate-400">{pago.fecha_pago} • {pago.metodo_pago}</p>
                        </div>
                        <p className="text-emerald-400 font-bold">${(pago.monto || 0).toLocaleString('es-CL')}</p>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Sin cuotas */}
              {cuotas?.length === 0 && (
                <Card className="p-8 bg-slate-800 border-slate-700 text-center">
                  <CreditCard className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No tienes cuotas registradas</p>
                </Card>
              )}
            </>
          )}
        </div>

        {/* Dialog de Pago por Transferencia */}
        <Dialog open={showPagoDialog} onOpenChange={setShowPagoDialog}>
          <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Banknote className="w-5 h-5 text-green-400" />
                Instrucciones de Pago
              </DialogTitle>
            </DialogHeader>
            
            {transaccionPago && (
              <div className="space-y-4">
                {/* Código de transacción */}
                <div className="bg-green-900/30 border border-green-700/50 rounded-lg p-4 text-center">
                  <p className="text-sm text-green-300 mb-2">Tu código de pago es:</p>
                  <div className="flex items-center justify-center gap-2">
                    <p className="text-3xl font-bold text-green-400 font-mono tracking-wider">
                      {transaccionPago.transaccion?.codigo}
                    </p>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => copyToClipboard(transaccionPago.transaccion?.codigo)}
                      className="text-green-400 hover:text-green-300"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-green-300/70 mt-2">
                    Incluye este código en la glosa de tu transferencia
                  </p>
                </div>

                {/* Monto a pagar */}
                <div className="bg-blue-900/30 border border-blue-700/50 rounded-lg p-4 text-center">
                  <p className="text-sm text-blue-300 mb-1">Monto a pagar:</p>
                  <p className="text-3xl font-bold text-white">
                    ${transaccionPago.transaccion?.monto?.toLocaleString('es-CL')}
                  </p>
                </div>

                {/* Datos bancarios */}
                {transaccionPago.datos_bancarios ? (
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <p className="text-sm font-semibold text-white mb-3">Datos para la transferencia:</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Banco:</span>
                        <span className="text-white font-medium">{transaccionPago.datos_bancarios.banco}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Tipo Cuenta:</span>
                        <span className="text-white font-medium capitalize">{transaccionPago.datos_bancarios.tipo_cuenta}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">N° Cuenta:</span>
                        <span className="text-white font-medium font-mono">{transaccionPago.datos_bancarios.numero_cuenta}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">RUT:</span>
                        <span className="text-white font-medium">{transaccionPago.datos_bancarios.rut_titular}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Titular:</span>
                        <span className="text-white font-medium">{transaccionPago.datos_bancarios.nombre_titular}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Correo:</span>
                        <span className="text-white font-medium">pagos@kinosis.cl</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-amber-900/30 border border-amber-700/50 rounded-lg p-4">
                    <p className="text-amber-400 text-sm text-center">
                      El club no ha configurado sus datos bancarios. Contacta al administrador.
                    </p>
                  </div>
                )}

                {/* Instrucciones */}
                <div className="bg-slate-900/50 rounded-lg p-3">
                  <p className="text-xs text-slate-400 leading-relaxed">
                    <strong className="text-white">Importante:</strong> Incluye el código{' '}
                    <strong className="text-green-400">{transaccionPago.transaccion?.codigo}</strong>{' '}
                    en el mensaje o glosa de tu transferencia. Tu pago será confirmado automáticamente.
                  </p>
                  <p className="text-xs text-slate-500 mt-2">
                    Código válido hasta: {transaccionPago.transaccion?.expira}
                  </p>
                </div>

                {/* Alternativa: Código del Club + RUT */}
                {clubInfo?.codigo_pago && (
                  <div className="bg-blue-900/30 border border-blue-700/50 rounded-lg p-3">
                    <p className="text-xs text-blue-300 leading-relaxed">
                      <strong>Alternativa:</strong> Si prefieres, también puedes usar este formato permanente:
                    </p>
                    <p className="text-sm text-white mt-1 font-medium">
                      {clubInfo.codigo_pago} {integrante?.rut?.replace(/\./g, '') || 'TU-RUT'}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Ejemplo: {clubInfo.codigo_pago} 12345678-9
                    </p>
                  </div>
                )}

                <Button onClick={() => setShowPagoDialog(false)} className="w-full">
                  Entendido
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Formulario de login
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center py-8 px-4">
      <div className="max-w-md w-full">
        {/* Header del Club */}
        <Card className="p-6 bg-slate-800 border-slate-700 mb-6 text-center">
          {clubInfo?.logo ? (
            <img src={clubInfo.logo} alt="Logo" className="w-24 h-24 rounded-xl object-cover mx-auto mb-4" />
          ) : (
            <div className="w-24 h-24 rounded-xl bg-purple-600/30 flex items-center justify-center mx-auto mb-4">
              <DisciplinaIcon className="w-12 h-12 text-purple-400" />
            </div>
          )}
          <h1 className="text-2xl font-bold text-white">{clubInfo?.nombre}</h1>
          <p className="text-purple-400 capitalize">{clubInfo?.disciplina?.replace('_', ' ')}</p>
        </Card>

        {/* Formulario */}
        <Card className="p-6 bg-slate-800 border-slate-700">
          <h2 className="text-lg font-semibold text-white mb-4">Consulta tu estado de cuenta</h2>
          
          <form onSubmit={handleConsulta} className="space-y-4">
            <div>
              <Label className="text-slate-300">RUT</Label>
              <Input
                placeholder="12.345.678-9"
                value={formData.rut}
                onChange={(e) => setFormData({ ...formData, rut: formatRut(e.target.value) })}
                className="bg-slate-700 border-slate-600 text-white"
                data-testid="input-rut"
              />
            </div>
            
            <div>
              <Label className="text-slate-300">Correo electrónico</Label>
              <Input
                type="email"
                placeholder="tu@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                data-testid="input-email"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full bg-purple-600 hover:bg-purple-700"
              disabled={submitting}
              data-testid="btn-consultar"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Consultando...
                </>
              ) : (
                'Consultar'
              )}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
